import { Dialog } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import toast from "react-hot-toast";
import InputTextModal from "../../components/clientServiceSubscriptionTask/modalGeneralComponents/InputTextModal";
import ButtonModal from "../../components/clientServiceSubscriptionTask/updateClientServiceSubscriptionTaskModal/ButtonModal";
import { isValidLink } from "../../helpers/urlRegexValidator";
import { updateChecklistItem } from "../../services/updateChecklistItem";
import { useParams } from "react-router-dom";

const EditItemModal = ({
    checklistItemToEdit,
    setChecklistItemToEdit,
    open,
    setOpen,
    retrieveChecklist,
}) => {
    const completeButtonRef = useRef(null);
    const params = useParams();

    // single link handling
    const [newLinkUrl, setNewLinkUrl] = useState("");

    // sync initial link
    useEffect(() => {
        if (open) {
            const firstLink = checklistItemToEdit.links?.[0]?.url || "";
            setNewLinkUrl(firstLink);
        }
    }, [open, checklistItemToEdit.links]);

    const onHandleChange = (e) => {
        const { name, value } = e.target;
        setChecklistItemToEdit((prev) => ({ ...prev, [name]: value }));
    };

    const handleChangeLink = (e) => {
        const url = e.target.value;
        setNewLinkUrl(url);
        const linkObj = { name: url, url };
        setChecklistItemToEdit((prev) => ({
            ...prev,
            links: url.trim() ? [linkObj] : [],
        }));
    };

    const onHandleSubmit = async (e) => {
        e.preventDefault();
        if (!checklistItemToEdit.description.trim()) {
            toast.error("Name is required.");
            return;
        }
        if (newLinkUrl.trim() && !isValidLink(newLinkUrl)) {
            toast.error("Please enter a valid URL.");
            return;
        }
        const response = await updateChecklistItem({
            id: checklistItemToEdit.id,
            task_id: params.taskId,
            description: checklistItemToEdit.description,
            order: checklistItemToEdit.order,
            links: JSON.stringify(checklistItemToEdit.links),
        });
        if (response.data.success) {
            toast.success(response.data.message);
            retrieveChecklist();
            setOpen(false);
        } else {
            toast.error(response.data.message);
        }
    };

    const onHandleCancel = () => setOpen(false);

    return (
        <AnimatePresence>
            {open && (
                <Dialog
                    initialFocus={completeButtonRef}
                    static
                    as={motion.div}
                    open={open}
                    onClose={onHandleCancel}
                    className="fixed inset-0 z-50"
                >
                    {/* Backdrop */}
                    <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

                    {/* Full-screen panel */}
                    <Dialog.Panel className="fixed inset-0 p-4 overflow-y-auto bg-white">
                        <div className="flex items-center justify-between mb-4">
                            <Dialog.Title className="text-xl font-semibold text-gray-900">
                                Update Checklist Item
                            </Dialog.Title>
                            <button
                                onClick={onHandleCancel}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                X
                            </button>
                        </div>

                        <form onSubmit={onHandleSubmit} className="space-y-6">
                            <InputTextModal
                                required
                                label="Name"
                                name="description"
                                onChange={onHandleChange}
                                value={checklistItemToEdit.description}
                            />

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Link URL (optional)
                                </label>
                                <input
                                    type="url"
                                    name="url"
                                    value={newLinkUrl}
                                    onChange={handleChangeLink}
                                    className="w-full p-2 mt-1 border border-gray-200 rounded shadow-sm focus:outline-none"
                                />
                            </div>

                            <div className="flex space-x-4">
                                <ButtonModal
                                    name="Cancel"
                                    type="button"
                                    onClick={onHandleCancel}
                                    className="flex-1 px-4 py-2 text-gray-700 bg-white border rounded shadow hover:bg-gray-50"
                                />
                                <ButtonModal
                                    name="Save"
                                    type="submit"
                                    ref={completeButtonRef}
                                    className="flex-1 px-4 py-2 text-white bg-teal-500 rounded shadow hover:bg-teal-600"
                                />
                            </div>
                        </form>
                    </Dialog.Panel>
                </Dialog>
            )}
        </AnimatePresence>
    );
};

export default EditItemModal;
